// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

import { EVENTS_DATA_TYPES } from './constants/eventDataTypes';
import syncProcessActivities from './Utils/ServiceWorker/syncProcessActivities';

let hasReloadedForUpdate = false;

function reloadWindowOnce() {
  if (hasReloadedForUpdate) {
    return;
  }

  hasReloadedForUpdate = true;
  window.location.reload();
}

function listenForControllerChangeOnce() {
  navigator.serviceWorker.addEventListener('controllerchange', reloadWindowOnce, {
    once: true,
  });
}

// #region Service worker register/Unregister
// Register the service worker for the production environment.
export function register(config) {
  if ('serviceWorker' in navigator) {
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets
      return;
    }

    // Register the service worker as early as possible so update checks + precaching
    // can happen in parallel with app loading.
    const swUrl = `${process.env.PUBLIC_URL}/serviceWorker.js`;
    if (isLocalhost) {
      // This is running on localhost. Let's check if a service worker still exists or not.
      checkValidServiceWorker(swUrl, config);
      // Add some additional logging to localhost, pointing developers to the
      // service worker/PWA documentation.
      navigator.serviceWorker.ready.then(() => {
        console.log('This web app is being served cache-first by a service worker.');
      });
    } else {
      // Is not localhost. Just register service worker
      registerValidSW(swUrl, config);
    }
  }
}

// Unregister Service Worker
export async function unregister() {
  if ('serviceWorker' in navigator) {
    try {
      let registration = await navigator.serviceWorker.ready;
      registration.unregister();
    } catch (error) {
      console.error(error.message);
    }
  }
}

// #endregion

// #region Service worker wrappers

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/,
    ),
);

// Register Service worker. If there is an update in the Service worker
// user will be notified , otherwise service worker activated successfully.
function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              if (registration && registration.waiting) {
                listenForControllerChangeOnce();
                registration.waiting.postMessage({
                  type: EVENTS_DATA_TYPES.SKIP_WAITING,
                });
              }
              // At this point, the updated precached content has been fetched,
              // but the previous service worker will still serve the older
              // content until all client tabs are closed.
              console.log(
                'New content is available and will be used when all ' +
                  'tabs for this page are closed.',
              );

              // Execute callback
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // At this point, everything has been precached.
              // It's the perfect time to display a
              // "Content is cached for offline use." message.
              console.log('Content is cached for offline use.');
              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
          if (installingWorker.state === 'activated') {
            if (navigator.serviceWorker.controller) {
              let activities = syncProcessActivities();
              navigator.serviceWorker.controller.postMessage({
                type: EVENTS_DATA_TYPES.PERIODIC_SYNC_ACTIVITIES,
                activities,
              });
            }
          }
        };
      };
      if (registration && registration.waiting) {
        listenForControllerChangeOnce();
        registration.waiting.postMessage({
          type: EVENTS_DATA_TYPES.SKIP_WAITING,
        });
      }
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

// Check the service worker by fetching call on the Service worker.
// If response get from the file then call register SW otherwise unregister
// the service worker.
function checkValidServiceWorker(swUrl, config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log(
        'No internet connection found. App is running in offline mode.',
      );
    });
}

// #endregion
